/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Observable, Subject, catchError, of, scan, switchMap, tap } from 'rxjs';

import { WsClientService } from './ws-client.service';
import { AppPhase, DealerAction, FlagStateType, SupervisorAction } from '../models/communication/app-enums.model';
import {
    AppEventType,
    CardBurnEvent,
    CardDealtEvent,
    ChatMessageEvent,
    FlagStateChangeEvent,
    GamePhaseChangeEvent,
    GameResultChangeEvent,
    GameStateChangeEvent,
    LoginChangeEvent,
    NewRoundPreparationPhaseChangeEvent,
    PlayerCountChangeEvent,
    RoundErrorPhaseChangeEvent,
    SupervisorStateChangeEvent
} from '../models/communication/app-events.model';
import {
    ChangeGameRequestMessage,
    DealerActionRequestMessage,
    DealerRequestType,
    GetChatMessagesResponseMessage,
    GetGamesResponseMessage,
    GetHistoryRequestMessage,
    GetMockWorkerRequestMessage,
    GetTableGroupsResponseMessage,
    RegisterWorkerRequestMessage,
    RegisterWorkerResponseMessage,
    ResultChangeRequestMessage,
    SetFlagStateRequestMessage,
    SupervisorActionRequestMessage
} from '../models/communication/app-requests.model';
import {
    AppState,
    FlagsState,
    Game,
    HistoryGame,
    InitialTableState,
    TableState,
    hasOnlyNextCardPosition
} from '../models/communication/app-state.model';
import { CardsChanged } from '../models/communication/result-change.model';
import { YEventPayload } from '../models/y-protocol.model';

@Injectable({
    providedIn: 'root'
})
export class DealerService {
    private static readonly maxChatMessageDisplayCount = 50;

    private readonly stateSubject = new Subject<AppState>();
    private state!: AppState;

    constructor(private readonly wsClient: WsClientService<AppState>) {}

    get gameInProgress(): boolean {
        return (
            this.state.roundState.phase === AppPhase.DealingCards ||
            this.state.roundState.phase === AppPhase.Decision ||
            this.state.roundState.phase === AppPhase.RoundEnd
        );
    }

    setWsUrl(wsUrl: string): void {
        this.wsClient.setWsUrl(wsUrl);
    }

    start(): void {
        const initDataObservable = this.getInitData();

        initDataObservable
            .pipe(
                tap((state) => (this.state = state)),
                switchMap(async () => Promise.all([this.sendGetChatMessagesRequest(), this.sendGetGamesRequest()])),
                tap(([chatMessages, games]) => {
                    this.state = { ...this.state, chatMessages, games };
                    this.initInitialState();
                    this.stateSubject.next(this.state);
                }),
                catchError((error) => {
                    console.error('[dealerService] failed to init data', error);
                    return of(undefined);
                })
            )
            .subscribe();

        initDataObservable.pipe(scan((acc: number) => acc + 1, 0)).subscribe((totalEmissions: number) => {
            if (totalEmissions > 1) {
                // This occurs when the server reconnects and re-sends the initialization data.
                // To avoid occasional connection issues, reload the page and restart the console after the second occurrence.
                //
                // Problem: After emitting initData the second time, issues may occur while fetching additional data
                // (e.g. games, chat messages), causing the wsClient to throw a '[ws] not connected' error.
                window.location.reload();
            }
        });

        this.getEventsObservable().subscribe((yEventPayload) => this.handleYEvent(yEventPayload));
    }

    getInitData(): Observable<AppState> {
        return this.wsClient.getInitData();
    }

    getEventsObservable(): Observable<YEventPayload<AppEventType>> {
        return this.wsClient.getEventsObservable() as Observable<YEventPayload<AppEventType>>;
    }

    getClosedConnectionsObservable(): Observable<number> {
        return this.wsClient.getClosedConnectionsObservable();
    }

    getStateObservable(): Observable<AppState> {
        return this.stateSubject.asObservable();
    }

    getState(): AppState {
        return this.state;
    }

    initInitialState(): void {
        if (this.state.tableState && Object.keys(this.state.tableState).length > 0) {
            this.state.initialTableState = this.normalizeInitialData(this.state.tableState);
            if (this.state.tableState.nextCardPosition !== undefined) {
                this.handleCardDealt({
                    nextCardPosition: this.state.tableState.nextCardPosition
                });
            }
        }
    }

    async sendSupervisorActionRequest(action: SupervisorAction): Promise<void> {
        const data: SupervisorActionRequestMessage = { action };
        await this.wsClient.sendRequestMessage(DealerRequestType.SupervisorAction, data);
    }

    async sendDealerActionRequest(action: DealerAction): Promise<void> {
        const data: DealerActionRequestMessage = { action };
        await this.wsClient.sendRequestMessage(DealerRequestType.DealerAction, data);
    }

    async sendSetFlagStateRequest(flag: FlagStateType, state: boolean): Promise<void> {
        const data: SetFlagStateRequestMessage = { flag, state };
        await this.wsClient.sendRequestMessage(DealerRequestType.SetFlagState, data);
        this.updateLocalFlagState(flag, state);
    }

    async sendChangeGameRequest(gameId: Game['id'], isUnfinished: boolean): Promise<void> {
        const data: ChangeGameRequestMessage = isUnfinished ? { continueUnfinishedGameId: gameId } : { gameId };
        await this.wsClient.sendRequestMessage(DealerRequestType.ChangeGame, data);
    }

    async sendResultChangeRequest(cards: CardsChanged): Promise<void> {
        const data: ResultChangeRequestMessage = { cards };
        await this.wsClient.sendRequestMessage(DealerRequestType.ChangeGameResult, data);
    }

    async sendCancelSupervisorActionRequest(): Promise<void> {
        await this.wsClient.sendRequestMessage(DealerRequestType.CancelSupervisorAction);
    }

    async sendGetChatMessagesRequest(): Promise<ChatMessageEvent[]> {
        const response: GetChatMessagesResponseMessage = await this.wsClient.sendRequestMessage<
            DealerRequestType.GetChatMessages,
            undefined,
            GetChatMessagesResponseMessage
        >(DealerRequestType.GetChatMessages);

        return response.messages;
    }

    async sendGetGamesRequest(): Promise<AppState['games']> {
        const response: GetGamesResponseMessage = await this.wsClient.sendRequestMessage<
            DealerRequestType.GetAvailableGames,
            undefined,
            GetGamesResponseMessage
        >(DealerRequestType.GetAvailableGames);

        return { availableGames: response.games, unfinishedGame: response.unfinishedGame };
    }

    async getHistory(max?: number): Promise<HistoryGame[]> {
        const request: GetHistoryRequestMessage = {};
        if (max !== undefined) {
            request.max = max;
        }
        const response = await this.wsClient.sendRequestMessage<
            DealerRequestType.GetTableHistory,
            GetHistoryRequestMessage,
            { history: HistoryGame[] }
        >(DealerRequestType.GetTableHistory, request);

        return response.history;
    }

    async sendRegisterWorkerRequest(data: RegisterWorkerRequestMessage): Promise<RegisterWorkerResponseMessage> {
        const response: RegisterWorkerResponseMessage = await this.wsClient.sendRequestMessage<
            DealerRequestType.RegisterWorker,
            RegisterWorkerRequestMessage,
            RegisterWorkerResponseMessage
        >(DealerRequestType.RegisterWorker, data);

        return {
            success: true,
            error: response.error || ''
        };
    }

    async getTableGroups(): Promise<string[]> {
        const request: GetTableGroupsResponseMessage = { tableGroups: [] };

        const response = await this.wsClient.sendRequestMessage<DealerRequestType.GetTableGroups, GetTableGroupsResponseMessage, string[]>(
            DealerRequestType.GetTableGroups,
            request
        );

        return response;
    }

    async getWorkerData(id: string, exist: boolean = false): Promise<void> {
        const request: GetMockWorkerRequestMessage = { id, exist };

        await this.wsClient.sendRequestMessage(DealerRequestType.GetMockWorker, request);
    }

    private updateLocalFlagState(flag: FlagStateType, state: boolean): void {
        this.state.flagsState[this.flagStateTypeToPropertyName(flag)] = state;
        this.stateSubject.next({ ...this.state });
    }

    private normalizeInitialData(tableState: TableState): InitialTableState | undefined {
        if (hasOnlyNextCardPosition(tableState)) {
            return undefined;
        }

        return {
            ...tableState,
            faceDownCards: tableState.faceDownCards?.map(([index, card]) => ({ card, index })) || []
        };
    }

    private flagStateTypeToPropertyName(flag: FlagStateType): keyof FlagsState {
        const firstLetter = flag.charAt(0);
        const normalizedFlag = flag.replace(firstLetter, firstLetter.toLowerCase());
        return normalizedFlag as keyof FlagsState;
    }

    private handleLoginChange(event: LoginChangeEvent): void {
        if (event.new) {
            const { id, name, accessLevel } = event.new;
            this.state.worker = { id, name, accessLevel };
        } else {
            this.state.worker = undefined;
        }
    }

    private handleSupervisorStateChange(event: SupervisorStateChangeEvent): void {
        this.state.supervisorState = event;
    }

    private handleGamePhaseChange(event: GamePhaseChangeEvent): void {
        this.state.roundState.phase = event.phase;

        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        switch (event.phase) {
            case AppPhase.NewRoundPreparation:
                this.resetState();
                this.state.roundState.roundId = (event as NewRoundPreparationPhaseChangeEvent).roundId;
                break;
            case AppPhase.RoundVoid:
                this.resetState();
                break;
            case AppPhase.DealingCards:
                this.state.roundState.subPhase = event.subPhase;
                // Leveraging the fact that in all games,
                // the position of the first card is always zero.
                // This should be ideally sent from the backend.
                if (!this.state.tableUpdate) {
                    this.handleCardDealt({ nextCardPosition: 0 });
                }
                break;
            case AppPhase.RoundError:
                this.state.roundState.errorCode = (event as RoundErrorPhaseChangeEvent).errorCode;
                break;
        }
    }

    private resetState(): void {
        this.state = {
            ...this.state,
            tableUpdate: undefined,
            tableState: undefined,
            initialTableState: undefined,
            gameResultChange: undefined,
            shoeChangeState: undefined,
            flagsState: {
                chatEnabled: this.state.flagsState.chatEnabled
            }
        };
    }

    private handleGameStateChangeEvent(event: GameStateChangeEvent): void {
        this.state = { ...event.state, games: this.state.games, chatMessages: this.state.chatMessages };
        this.initInitialState();
    }

    private handleCardBurn(event: CardBurnEvent): void {
        this.state.shoeChangeState = event;
    }

    private handleCardDealt(event: CardDealtEvent): void {
        this.state.tableUpdate = event;
    }

    private handleCutCardDrawn(): void {
        this.state.shoeChangeState = { ...this.state.shoeChangeState, cutCardDrawn: true };
    }

    private handleGameResultChange(event: GameResultChangeEvent): void {
        this.state.gameResultChange = event;
        // The nextCardPosition logic is specific to individual games.
        // It is emitted as an event and handled within the respective game logic.
        if (this.state.gameResultChange.nextCardPosition !== undefined) {
            this.handleCardDealt({ nextCardPosition: this.state.gameResultChange.nextCardPosition });
        }
    }

    private handleFlagStateChange(event: FlagStateChangeEvent): void {
        this.state.flagsState[this.flagStateTypeToPropertyName(event.flag)] = event.state;
    }

    private handlePlayerCountChange(event: PlayerCountChangeEvent): void {
        this.state.playerCount = event.count;
    }

    private handleChatMessage(event: ChatMessageEvent): void {
        const messages = this.state.chatMessages || [];

        this.state.chatMessages =
            // Remove the oldest message (last element) if too big
            messages.length < DealerService.maxChatMessageDisplayCount ? [event, ...messages] : [event, ...messages.slice(0, -1)];
    }

    /**
     * In this method we only handle events that change the state of the game.
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private handleYEvent(yEventPayload: YEventPayload<AppEventType>): void {
        const { data: event, name: appEventType } = yEventPayload;

        switch (appEventType) {
            case AppEventType.LoginChange:
                this.handleLoginChange(event as LoginChangeEvent);
                break;
            case AppEventType.SupervisorStateChange:
                this.handleSupervisorStateChange(event as SupervisorStateChangeEvent);
                break;
            case AppEventType.GamePhaseChange:
                this.handleGamePhaseChange(event as GamePhaseChangeEvent);
                break;
            case AppEventType.GameStateChange:
                this.handleGameStateChangeEvent(event as GameStateChangeEvent);
                break;
            case AppEventType.CardBurn:
                this.handleCardBurn(event as CardBurnEvent);
                break;
            case AppEventType.CardDealt:
                this.handleCardDealt(event as CardDealtEvent);
                break;
            case AppEventType.CutCardDrawn:
                this.handleCutCardDrawn();
                break;
            case AppEventType.GameResultChange:
                this.handleGameResultChange(event as GameResultChangeEvent);
                break;
            case AppEventType.FlagStateChange:
                this.handleFlagStateChange(event as FlagStateChangeEvent);
                break;
            case AppEventType.PlayerCountChange:
                this.handlePlayerCountChange(event as PlayerCountChangeEvent);
                break;
            case AppEventType.ChatMessage:
                this.handleChatMessage(event as ChatMessageEvent);
                break;
            case AppEventType.Notification:
            default:
                return;
        }

        this.stateSubject.next({ ...this.state });
    }
}
